import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Paper, Typography, CircularProgress, Chip, Button,
  Table, TableBody, TableRow, TableCell, TableHead, TableContainer,
  TableSortLabel, alpha, useTheme
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { searchParliaments } from '../services/api';
import useTableSort from '../hooks/useTableSort';

// Raw (unformatted) sort keys for each sortable column header.
const PARLIAMENT_SORT_KEYS = {
  '#': (p) => Number(p.PID) || 0,
  Parliament: (p) => Number(p.PID) || 0,
  Election: (p) => p.DateElection || '',
  Opened: (p) => p.DateOpening || '',
  Dissolved: (p) => p.DateDissolution || '',
  Duration: (p) => {
    if (!p.DateOpening) return 0;
    const end = p.DateDissolution ? new Date(p.DateDissolution) : new Date();
    return end - new Date(p.DateOpening);
  },
  // #247 (1): Sessions column. Counts the nested `Sessions[]` array
  // populated by the Handbook parliaments scraper (#273).
  Sessions: (p) => (p.Sessions ? p.Sessions.length : 0),
};

export default function ParliamentsSearch() {
  const theme = useTheme();
  const [parliaments, setParliaments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchParliaments = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch all parliaments
        const data = await searchParliaments('*', 1, 100);
        if (data.hits) {
          // Sort by PID descending (most recent first)
          const sorted = [...data.hits].sort((a, b) => b.PID - a.PID);
          setParliaments(sorted);
        }
      } catch (err) {
        console.error('Failed to fetch parliaments:', err);
        setError('Failed to load parliaments data');
      }
      setLoading(false);
    };
    fetchParliaments();
  }, []);

  // User-sortable headers (#134); default order (by PID desc) is unchanged
  // until a header is clicked. Declared before any early return so the
  // Hook order is stable.
  const { order, orderBy, sortedRows, handleSort } = useTableSort(
    parliaments,
    (p, col) => (PARLIAMENT_SORT_KEYS[col] ? PARLIAMENT_SORT_KEYS[col](p) : ''),
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const isCurrent = (parliament) => {
    return !parliament.DateDissolution || new Date(parliament.DateDissolution) > new Date();
  };

  const calculateDuration = (start, end) => {
    if (!start) return null;
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();
    const diffTime = Math.abs(endDate - startDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const years = Math.floor(diffDays / 365);
    const months = Math.floor((diffDays % 365) / 30);
    if (years > 0) {
      return months > 0 ? `${years}y ${months}m` : `${years}y`;
    }
    return `${months}m`;
  };

  const getOrdinalNumber = (pid) => {
    if (pid === 1) return '1st';
    if (pid === 2) return '2nd';
    if (pid === 3) return '3rd';
    return `${pid}th`;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">{error}</Typography>
      </Box>
    );
  }

  // Find current parliament
  const currentParliament = parliaments.find(p => isCurrent(p));

  const SortableTh = ({ col, sx }) => (
    <TableCell sx={{ fontWeight: 600, ...sx }} sortDirection={orderBy === col ? order : false}>
      {PARLIAMENT_SORT_KEYS[col] ? (
        <TableSortLabel
          active={orderBy === col}
          direction={orderBy === col ? order : 'asc'}
          onClick={() => handleSort(col)}
        >
          {col}
        </TableSortLabel>
      ) : col}
    </TableCell>
  );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <AccountBalanceIcon sx={{ fontSize: 40, color: 'primary.main' }} />
        <Box>
          <Typography variant="h4">Australian Parliaments</Typography>
          <Typography variant="body2" color="text.secondary">
            Chronology of all Australian Federal Parliaments since Federation (1901)
          </Typography>
        </Box>
      </Box>

      {/* Current Parliament Highlight */}
      {currentParliament && (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.success.main, 0.08)} 0%, ${alpha(theme.palette.success.main, 0.02)} 100%)`,
            border: '1px solid',
            borderColor: alpha(theme.palette.success.main, 0.2)
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Chip label="Current Parliament" size="small" color="success" />
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.dark', mb: 1 }}>
                {getOrdinalNumber(currentParliament.PID)} Parliament
              </Typography>
              <Typography variant="body1" color="text.secondary">
                <CalendarTodayIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                Opened {formatDate(currentParliament.DateOpening)}
                {' • '}
                Election held {formatDate(currentParliament.DateElection)}
              </Typography>
            </Box>
            <Button
              component={RouterLink}
              to={`/parliaments/${currentParliament.PID}`}
              variant="contained"
              color="success"
              endIcon={<ArrowForwardIcon />}
              sx={{ borderRadius: 2 }}
            >
              View Statistics
            </Button>
          </Box>
        </Paper>
      )}

      {/* Parliaments Chronology Table */}
      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'grey.50' }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Parliamentary Chronology
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {parliaments.length} parliaments from Federation to present
          </Typography>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <SortableTh col="#" sx={{ width: '5%' }} />
                <SortableTh col="Parliament" sx={{ width: '25%' }} />
                <SortableTh col="Election" sx={{ width: '15%' }} />
                <SortableTh col="Opened" sx={{ width: '15%' }} />
                <SortableTh col="Dissolved" sx={{ width: '13%' }} />
                <SortableTh col="Duration" sx={{ width: '8%' }} />
                <SortableTh col="Sessions" sx={{ width: '7%' }} />
                <TableCell sx={{ fontWeight: 600, width: '12%', textAlign: 'right' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedRows.map((parliament) => {
                const current = isCurrent(parliament);
                return (
                  <TableRow
                    key={parliament.PID}
                    sx={{
                      bgcolor: current ? alpha(theme.palette.success.main, 0.05) : 'inherit',
                      '&:hover': { bgcolor: current ? alpha(theme.palette.success.main, 0.1) : 'action.hover' }
                    }}
                  >
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {parliament.PID}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button
                          component={RouterLink}
                          to={`/parliaments/${parliament.PID}`}
                          sx={{
                            p: 0,
                            minWidth: 'auto',
                            textTransform: 'none',
                            fontWeight: 600,
                            color: current ? 'success.dark' : 'primary.main'
                          }}
                        >
                          {parliament.ParliamentOrdinal || parliament.Name}
                        </Button>
                        {current && (
                          <Chip label="Current" size="small" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatShortDate(parliament.DateElection)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatShortDate(parliament.DateOpening)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {current ? (
                          <Chip label="In session" size="small" variant="outlined" color="success" sx={{ height: 20, fontSize: '0.7rem' }} />
                        ) : (
                          formatShortDate(parliament.DateDissolution)
                        )}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {calculateDuration(parliament.DateOpening, parliament.DateDissolution)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {parliament.Sessions?.length ?? 0}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ textAlign: 'right' }}>
                      <Button
                        component={RouterLink}
                        to={`/parliaments/${parliament.PID}`}
                        size="small"
                        variant="outlined"
                        sx={{ borderRadius: 2 }}
                      >
                        View Stats
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
